import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Loader2, HardHat, Plus, Building2, Users, MapPin, Globe } from "lucide-react";
import { VendorPerformance, VendorReviews, RatingBadge, InsuredPill } from "@/components/VendorRatings";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};

function VendorProfile({ tenantId, vendorId, onChanged }) {
  const { me } = useDash();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tenants/${tenantId}/field-services/vendor-search/${vendorId}`);
    setD(data);
  }, [tenantId, vendorId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;
  const v = d.vendor;

  const rate = async ({ stars, note }) => {
    try {
      await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/reviews`, { stars, note });
      toast.success(`Rating saved — other ${V.lcs("org")} can see it`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that rating"); }
  };
  const remove = async (rid) => {
    try {
      await api.delete(`/tenants/${tenantId}/field-services/vendors/${vendorId}/reviews/${rid}`);
      toast.success("Rating removed"); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove it"); }
  };
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/adopt`, {});
      toast.success(`${v.name} added — approve them to start sending work${data?.held ? `. Their text goes out ${data.held.send_at_local} (after hours)` : ""}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them here"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="fv-profile">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-bold text-ink">{v.name}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <InsuredPill ok={v.coi_ok} expires={v.coi_expires_on} onFile={v.coi_on_file} />
            <span className="inline-flex items-center gap-1 text-xs text-ink/50"><Users className="h-3.5 w-3.5" />{v.crew_ready} crew ready</span>
            <span className="inline-flex items-center gap-1 text-xs text-ink/50"><Building2 className="h-3.5 w-3.5" />{v.properties} propert{v.properties === 1 ? "y" : "ies"}</span>
            {v.website && (
              <a href={v.website} target="_blank" rel="noreferrer" data-testid="fv-website"
                className="inline-flex items-center gap-1 text-xs font-semibold text-ink/50 hover:text-ink">
                <Globe className="h-3.5 w-3.5" /> Website
              </a>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(v.trades || []).map((t) => (
              <span key={t} className="rounded-full bg-sand-100 px-2.5 py-1 text-xs font-semibold text-ink/60">{CAT_LABEL[t] || t}</span>
            ))}
          </div>
        </div>
        {v.linked_here ? (
          <span data-testid="fv-already-here" className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">Already yours</span>
        ) : (
          <button data-testid="fv-add-here" onClick={add} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl bg-redline-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-redline-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to this {V.lc("org")}
          </button>
        )}
      </div>

      <VendorPerformance scorecard={d.scorecard} here={d.here} testid="fv-performance" />
      <VendorReviews reviews={d.reviews} rating={d.rating} canRate={d.can_rate} myReview={d.my_review}
        onRate={rate} onDelete={remove} meId={me} testid="fv-reviews" />

      {!d.can_rate && !v.linked_here && (
        <p className="text-xs text-ink/45" data-testid="fv-rate-hint">
          Add them to this {V.lc("org")} to leave a rating — ratings come from work you've actually had done.
        </p>
      )}
    </div>
  );
}

export default function FindVendor() {
  const { tenantId, tenant } = useDash();
  const [q, setQ] = useState("");
  const [trade, setTrade] = useState("");
  const [area, setArea] = useState(false);
  const [mine, setMine] = useState(false);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    const { data } = await api.get(`/tenants/${tenantId}/field-services/vendor-search`, {
      params: { q, trade, area: area ? 1 : 0, mine: mine ? 1 : 0 },
    });
    setD(data);
  }, [tenantId, q, trade, area, mine]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const rows = d?.vendors || [];

  return (
    <div className="space-y-6" data-testid="find-vendor-page">
      <div>
        <PageTitle title="Find a vendor" about={<>Every company approved across the Ask Redline network — searchable by name or trade, with
          what other {V.lcs("org")} thought of them. Add one to {tenant?.name || `this ${V.lc("org")}`} when you're ready.</>} />
      </div>

      <div className="rounded-2xl border border-sand-200 bg-white p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/30" />
          <input data-testid="fv-search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="Search by company or trade — e.g. Sparkle, plumbing, pool"
            className="w-full rounded-xl border border-sand-300 bg-white py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <button data-testid="fv-trade-all" onClick={() => setTrade("")}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${!trade ? "bg-ink text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>All trades</button>
          {(d?.trades || []).map((t) => (
            <button key={t} data-testid={`fv-trade-${t}`} onClick={() => setTrade(trade === t ? "" : t)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${trade === t ? "bg-redline-500 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
              {CAT_LABEL[t] || t}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs font-semibold text-ink/60">
          {d?.market_set && (
            <label className="inline-flex cursor-pointer items-center gap-2">
              <input type="checkbox" data-testid="fv-area-toggle" checked={area} onChange={(e) => setArea(e.target.checked)} className="accent-redline-500" />
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Serves {d.market}</span>
            </label>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input type="checkbox" data-testid="fv-mine-toggle" checked={mine} onChange={(e) => setMine(e.target.checked)} className="accent-redline-500" />
            <span>Only ours</span>
          </label>
          <span className="text-ink/35" data-testid="fv-count">{!d ? "Searching…" : `${rows.length} compan${rows.length === 1 ? "y" : "ies"}`}</span>
        </div>
      </div>

      {!d ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>
      ) : rows.length === 0 ? (
        <div data-testid="fv-empty" className="rounded-2xl border border-dashed border-sand-300 py-16 text-center">
          <HardHat className="mx-auto h-8 w-8 text-ink/20" />
          <p className="mt-3 text-sm text-ink/50">
            Nothing matches that. Try a different trade or clear the filters — or ask Redline to invite a company you already work with.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {rows.map((v) => (
            <button key={v.id} data-testid={`fv-card-${v.id}`} onClick={() => setOpen(v.id)}
              className="rounded-2xl border border-sand-200 bg-white p-4 text-left transition-colors hover:border-ink/25">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-heading text-base font-bold text-ink">{v.name}</div>
                  <div className="mt-1"><RatingBadge rating={v.rating} testid={`fv-rating-${v.id}`} /></div>
                </div>
                {v.linked_here && (
                  <span data-testid={`fv-yours-${v.id}`} className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Yours</span>
                )}
              </div>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {(v.trades || []).slice(0, 4).map((t) => (
                  <span key={t} className="rounded-full bg-sand-100 px-2 py-0.5 text-[11px] font-semibold text-ink/60">{CAT_LABEL[t] || t}</span>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <InsuredPill ok={v.coi_ok} expires={v.coi_expires_on} onFile={v.coi_on_file} />
                <span className="inline-flex items-center gap-1 text-xs text-ink/45"><Users className="h-3.5 w-3.5" />{v.crew_ready} crew</span>
                <span className="inline-flex items-center gap-1 text-xs text-ink/45"><Building2 className="h-3.5 w-3.5" />{v.properties}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Vendor</DialogTitle></DialogHeader>
          {open && <VendorProfile tenantId={tenantId} vendorId={open} onChanged={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
